<template>
    <div class="flex items-center gap-3">
        <slot name="avatar" />

        <div class="flex flex-col gap-0.5 justify-center" :class="{ 'min-w-0': props.truncateLines }">
            <div
                v-if="props.company"
                class="text-xs font-light leading-snug"
                :class="[companyColorClasses, strikeThroughClasses, truncateClasses]"
            >
                {{ props.company.name }}
            </div>
            <component
                :is="props.variant !== 'Inactive' && props.linkTarget ? 'router-link' : 'div'"
                :to="props.variant !== 'Inactive' ? props.linkTarget : undefined"
                class="text-md leading-none"
                :class="[nameClasses, strikeThroughClasses, linkClasses, truncateClasses]"
            >
                <template v-for="(part, index) in nameParts" :key="index">
                    <mark v-if="part.isMatch" class="rounded-xs bg-yellow-200 text-inherit">{{ part.text }}</mark>
                    <template v-else>{{ part.text }}</template>
                </template>
            </component>
            <div
                v-if="props.jobTitle"
                class="text-xs font-light leading-snug text-gray-500"
                :class="[strikeThroughClasses, truncateClasses]"
            >
                {{ props.jobTitle }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { splitByMatches } from '@/utils/splitByMatches';

export type CompanyType = 'Koelnmesse' | 'Koelncongress' | 'Other';
export type ActivationStatus = 'Active' | 'Inactive' | 'Deactivated';

type PersonInfoProps = {
    name: string;
    jobTitle?: string;
    company?: {
        name: string;
        type: CompanyType;
    };
    variant: ActivationStatus;
    linkTarget?: RouteLocationRaw;
    nameHighlight?: string;
    truncateLines?: boolean;
};

const props = withDefaults(defineProps<PersonInfoProps>(), {
    nameHighlight: '',
});

const nameParts = computed(() => splitByMatches(props.name, props.nameHighlight));

const truncateClasses = computed(() =>
    props.truncateLines ? 'overflow-x-clip text-ellipsis whitespace-nowrap' : undefined,
);

const BASE_LINK_CLASSES =
    'self-start cursor-pointer underline-offset-2 rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700';

const nameClasses = computed(() => {
    switch (props.variant) {
        case 'Active':
            return 'font-medium text-gray-700';

        case 'Inactive':
        case 'Deactivated':
            return 'font-light text-gray-500';

        default:
            throw new Error(`Unknown PersonInfo variant: ${props.variant satisfies never}`);
    }
});

const companyColorClasses = computed(() => {
    switch (props.variant) {
        case 'Active': {
            const isKoelnmesseGroup = props.company?.type === 'Koelnmesse' || props.company?.type === 'Koelncongress';
            return isKoelnmesseGroup ? 'text-koelnmesse' : 'text-gray-500';
        }

        case 'Inactive':
        case 'Deactivated':
            return 'text-gray-500';

        default:
            throw new Error(`Unknown PersonInfo variant: ${props.variant satisfies never}`);
    }
});

const strikeThroughClasses = computed(() => {
    switch (props.variant) {
        case 'Active':
        case 'Inactive':
            return '';

        case 'Deactivated':
            return 'line-through';

        default:
            throw new Error(`Unknown PersonInfo variant: ${props.variant satisfies never}`);
    }
});

const linkClasses = computed(() => {
    if (!props.linkTarget) {
        return '';
    }

    switch (props.variant) {
        case 'Active':
            return `${BASE_LINK_CLASSES} hover:underline active:underline active:text-gray-900`;

        // Inactive names are not links.
        case 'Inactive':
            return '';

        // `underline` and `line-through` are both text-decoration-line, so hovering has to
        // re-declare both instead of replacing the strikethrough with an underline.
        case 'Deactivated':
            return `${BASE_LINK_CLASSES} hover:[text-decoration-line:underline_line-through] active:[text-decoration-line:underline_line-through] active:text-gray-700`;

        default:
            throw new Error(`Unknown PersonInfo variant: ${props.variant satisfies never}`);
    }
});
</script>
